import React, {useState, useEffect, Fragment} from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import {useTranslation} from 'react-i18next'
import useStyles from 'pages/ProductOperationOrganization/Component/DialogRegistrationManualStyle'
import {
  fetchDetailProduct,
  fetchProductDetailApi,
  ListLicenses,
  registerManualLicense,
} from 'api/productApi'
import useFailureModal from 'features/modal/useFailureModal'
import useSuccessModal from 'features/modal/useSuccessModal'
import useProductManagement from 'features/productManagement/useProductManagement'
import {TYPE_INDICATOR_PRODUCT} from 'pages/ProductsPages/constant'
import useOrganization from 'features/org/useOrganization'
import {AppLoading} from 'AppLoading'

type DialogActiveProductProps = {
  open: boolean
  onClose: () => void
  orgName: string
  item: ListLicenses
  onUpdateLog: () => void
}

const DialogRegistrationManual = (props: DialogActiveProductProps) => {
  const {open, onClose, orgName, item, onUpdateLog} = props
  const {organization} = useOrganization()
  const [productName, setProductName] = useState('')
  const {getNameProduct, getPackageName, getPackageList} =
    useProductManagement()
  const classes = useStyles()
  const {t, i18n} = useTranslation()

  const LIST_REASON = [
    {
      label: 'IApprovalPayment',
      value: 'PaymentApproval',
    },
    {
      label: 'IRefundPayment',
      value: 'PaymentRefund',
    },
    {
      label: 'IAssignedByAdmin',
      value: 'AssignByAdmin',
    },
    {
      label: 'ICompensationPayment',
      value: 'Compensation',
    },
    {
      label: 'IFreePayment',
      value: 'PaymentFree',
    },
    {
      label: 'IAnalysisVolumeControl',
      value: 'Adjustment',
    },
    {
      label: 'IOther',
      value: 'Other',
    },
  ]
  const ICA_TYPE = [
    {
      label: 'IPaymentV1',
      value: 'amount',
    },
    {
      label: 'IDurationV1',
      value: 'deduction',
    },
  ]
  const [privateUse, setPrivateUse] = useState<string>(LIST_REASON[0].value)
  const [loading, setLoading] = useState(false)
  const [listPackageAmount, setListPackageAmount] = useState<
    {
      itemId: string
      amount: number
    }[]
  >([])
  const [listDeductionItemId, setListDeductionItemId] = useState<string[]>([])
  const [internalReason, setInternalReason] = useState('')
  const {onOpen: onOpenFail} = useFailureModal()
  const {onOpen: onOpenSuccess} = useSuccessModal()

  const filteredItems = item?.items.filter(
    (itm) => itm.offerQuantity < 10000000,
  )

  const listItems = filteredItems.reduce<
    Record<string, {id: string; packageName: string}>
  >((acc, {packageItemId}) => {
    const firstItem = filteredItems?.find(
      (item) => item.packageItemId === packageItemId,
    )

    acc[packageItemId] = {
      id: firstItem?.id || '',
      packageName: getPackageName(packageItemId),
    }
    return acc
  }, {})

  useEffect(() => {
    const generateDetail = async () => {
      const res = await fetchProductDetailApi({goodsId: item.goodsId})
      setProductName(
        getNameProduct(res.data.goods?.displayInfos || [])?.name || '',
      )
    }
    generateDetail()
    getPackageList({
      target: TYPE_INDICATOR_PRODUCT.ALL,
    })
  }, [])

  const getResultMessage = () => {
    if (listDeductionItemId.length === 0) {
      return t('IAnalysisCredited')
    }
    if (listDeductionItemId.length === Object.keys(listItems).length) {
      return t('IAnalysisDeducted')
    }
    return t('IAnalysisAdjusted')
  }

  const handleChangeAmount = (value: string, licenseId: string) => {
    setListPackageAmount((prevItems) => {
      const isPackageExist = prevItems.some((item) => item.itemId === licenseId)
      if (isPackageExist) {
        return prevItems.map((item) =>
          item.itemId === licenseId
            ? {...item, amount: value === '' ? 0 : Number(value)}
            : item,
        )
      }
      return [
        ...prevItems,
        {itemId: licenseId, amount: value === '' ? 0 : Number(value)},
      ]
    })
  }

  const handleManualRegistration = async () => {
    try {
      setLoading(true)
      const payload = {
        licenseId: item.id,
        data: {
          quantities: listPackageAmount.map((item) => ({
            ...item,
            amount: listDeductionItemId.includes(item.itemId)
              ? -item.amount
              : item.amount,
          })),
          reasonCode: privateUse,
          reason: internalReason,
          changedByName: organization?.fullUserName || '',
        },
      }
      const res = await registerManualLicense(payload)
      if (res.msg === 'SUCCESS') {
        onClose()
        onOpenSuccess(getResultMessage()).then(() => {
          onUpdateLog()
        })
        setInternalReason('')
        setListDeductionItemId([])
        setListPackageAmount([])
      }
    } catch (error) {
      onClose()
      onOpenFail(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} className={classes.container}>
      <div className={classes.closeButtonWrap}>
        <IconButton
          color='secondary'
          aria-label='favorite'
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </div>
      {loading ? (
        <div
          style={{
            minHeight: 500,
            position: 'relative',
          }}
        >
          <AppLoading loading />
        </div>
      ) : (
        <DialogContent className={classes.contentWrap}>
          <div className={classes.headerDialog}>{t('IManualRegistration')}</div>
          <div className={classes.infoCard}>
            <div className={classes.infoItem}>
              <span className={classes.infoLabel}>
                {t('ICustomerOrganizationName')}:
              </span>
              <span className={classes.infoValue}>{orgName}</span>
            </div>
            <div className={classes.infoItem}>
              <span className={classes.infoLabel}>
                {t('IProductNameSelect')}:
              </span>
              <span className={classes.infoValue}>{productName}</span>
            </div>
          </div>
          {Object.values(listItems).map((item) => (
            <Fragment key={item.id}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 15,
                }}
              >
                -{item.packageName}:
                <div className={classes.listItem}>
                  <FormControl variant='outlined'>
                    <Select
                      value={
                        listDeductionItemId.includes(item.id)
                          ? 'deduction'
                          : 'amount'
                      }
                      className={
                        i18n.language !== 'ko'
                          ? classes.searchSelectV1
                          : classes.searchSelectV2
                      }
                      input={
                        <OutlinedInput name='age' id='outlined-age-simple' />
                      }
                      onChange={(e) =>
                        e.target.value === 'deduction'
                          ? setListDeductionItemId((prev) => [...prev, item.id])
                          : setListDeductionItemId((prev) =>
                              prev.filter((id) => id !== item.id),
                            )
                      }
                    >
                      {ICA_TYPE.map((item) => (
                        <MenuItem value={item.value} key={item.value}>
                          {t(item.label)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <input
                    className={classes.input}
                    style={{
                      width: 100,
                    }}
                    type='number'
                    min={0}
                    onChange={(e) =>
                      handleChangeAmount(e.target.value, item.id)
                    }
                  />
                </div>
              </div>
            </Fragment>
          ))}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 15,
            }}
          >
            <span
              className={classes.infoTitle}
              style={{
                marginRight: 10,
              }}
            >
              {t('ICustomerManager')} :
            </span>
            <input
              className={classes.input}
              style={{
                width: 180,
              }}
              disabled
              value={organization?.fullUserName}
            />
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 15,
            }}
          >
            <span className={classes.infoTitle}>{t('IPrivateUse')}:</span>
            <div>
              <FormControl variant='outlined' style={{width: '100%'}}>
                <Select
                  value={privateUse}
                  onChange={(event: React.ChangeEvent<{value: unknown}>) =>
                    setPrivateUse(event.target.value as KeyOfEdfSearchKind)
                  }
                  style={{width: 180, marginRight: 0}}
                  className={classes.searchSelectV1}
                  input={<OutlinedInput name='age' id='outlined-age-simple' />}
                >
                  {LIST_REASON.map((item) => (
                    <MenuItem value={item.value} key={item.value}>
                      {t(item.label)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span
              className={classes.infoTitle}
              style={{
                marginRight: 10,
              }}
            >
              {t('IPrivateInternal')}:
            </span>
            <input
              className={classes.input}
              value={internalReason}
              onChange={(e) => setInternalReason(e.target.value)}
            />
          </div>
          <div className={classes.listBtn}>
            <Button color='default' variant='contained' onClick={onClose}>
              {t('ICancel')}
            </Button>
            <Button
              color='primary'
              variant='contained'
              onClick={handleManualRegistration}
              disabled={false}
            >
              {t('IConfirm')}
            </Button>
          </div>
        </DialogContent>
      )}
    </Dialog>
  )
}

export default DialogRegistrationManual
